import { APP } from '../../../../config';
import { getExternalLinkOrigin } from '../../../../modules/env';
import { ShareProvider } from '../../../../modules/share';
import { ssFetchJSON } from '../../../../modules/ssFetch';
import { IS_MINE_MESSAGE } from '../constants';
import store from '../../../../store';

const providerAtSalon = (provider: ShareProvider) => {
  const { name, biz_name: businessName } = provider;
  if (businessName && businessName !== name) {
    return `${name} at ${businessName}`;
  }
  return name;
};

/**
 * Return the share image for a provider or salon
 * @param {Object} provider A provider, or salon, object
 * @return {String} The full image URL to be shared.
 */
const getProfileShareImage = (provider: ShareProvider) => {
  const images = provider?.gallery_images || [];
  return (images && images.length) ? `${images[0].url}_720x540.jpg` : null;
};

/**
 * Normalizes the social sharing campaign, defaulting to Profile
 */
const getSocNetCampaign = campaign => {
  let result = campaign;
  if (campaign === 'profile' || campaign === 'booking_confirm_share' || !campaign) {
    result = (campaign || 'profile').toLowerCase();
  }
  return result === 'profile' ? 'Profile' : result;
};

const urlWithParams = (urlBase, params) => {
  const url = new URL(urlBase);
  Object.keys(params).forEach(key => {
    url.searchParams.set(key, params[key]);
  });
  return url;
};

/**
 * Determines the sharing platform URLs for a pro profile
 * This logic was migrated directly from Angular
 */
const getProfileSharingUrls = (proOrSalon, campaign?: string) => {
  if (!proOrSalon) {
    return {};
  }
  const defaultCampaign = campaign || proOrSalon.type === 'provider' ? 'profile' : 'salonProfile';
  const socNetCampaign = getSocNetCampaign(defaultCampaign);
  const isProvider = !!store.getState().user?.providerId;
  const sharer = isProvider ? 'Provider' : 'User';
  const urlBase = [
    getExternalLinkOrigin(),
    proOrSalon.type === 'provider' ? 'm/v' : 's',
    proOrSalon.vanity_url,
  ].join('/');
  const sharePlatformString = APP ? 'App' : 'Mobileweb';

  const makeUrl = params => urlWithParams(urlBase, params);

  return {
    facebook: makeUrl({
      utm_source: `FacebookShare${sharePlatformString}`,
      utm_medium: 'Social',
      utm_content: `FBS_${proOrSalon.vanity_url}`,
      utm_campaign: `FBS_${socNetCampaign}_${sharer}`,
    }).toString(),
    twitter: makeUrl({
      utm_source: `Tweet${sharePlatformString}`,
      utm_medium: 'Social',
      utm_content: `TWT_${proOrSalon.vanity_url}`,
      utm_campaign: `TWT_${socNetCampaign}_${sharer}`,
    }).toString(),
    sms: makeUrl({
      utm_campaign: `SMS_${campaign}_${sharer}`,
    }).toString(),
    email: makeUrl({
      utm_campaign: `email_${campaign}_${sharer}`,
    }).toString(),
    pinterest: makeUrl({
      utm_campaign: `Pin_${campaign}_${sharer}`,
      utm_source: `Pin${sharePlatformString}`,
    }).toString(),
  };
};

const urls = {
  profile: getProfileSharingUrls,
  review(provider, reviewId) {
    const providerUrlBase = `${getExternalLinkOrigin()}/feedback/${reviewId}/fb_graph_object`;
    const sharePlatformString = APP ? 'App' : 'Mobileweb';

    return {
      facebook: urlWithParams(providerUrlBase, {
        utm_source: `FacebookShare${sharePlatformString}`,
        utm_medium: 'Social',
        utm_content: `FBS_${provider.vanity_url}`,
        utm_campaign: 'FBS_Recommend_Provider',
      }),
    };
  },
};

/**
 * Determines the sharing platform messages for a pro profile
 * This logic was migrated directly from Angular
 */
const messages = {
  // Profile social sharing
  profile(provider, user, shareUrls) {
    const isMine = provider.id === user.providerId;
    const isMineMessage = 'My beauty services are now available online on StyleSeat.'
    + ' Click on the link to book with me today';
    const youllLikeMessage = `I think you'll like ${provider.name}, you can book an appointment online at`;
    return {
      sms: `${isMine ? isMineMessage : youllLikeMessage} ${shareUrls.sms}`,
      email: {
        subject: (user && user.first_name)
          ? (`${user.first_name} thinks you'll like StyleSeat`)
          : 'Book your next appointment online with StyleSeat',
        body: `${isMine ? isMineMessage : `${'Have you checked out StyleSeat? You can book online '
            + 'with the best beauty & wellness professionals in your neighborhood. '
             + "I think you'll like "}${providerAtSalon(provider)}, check it out at`
        } ${shareUrls.email}`,
      },
      facebook: {
        header: `Book an appointment online with ${provider.name} | StyleSeat`,
        text: isMine
          ? isMineMessage
          : `Book an appointment online with ${providerAtSalon(provider)} in ${provider.biz_city}`,
      },
      image: getProfileShareImage(provider),
      twitter: isMine
        ? isMineMessage
        : `I think you'll like ${provider.name}, click the link to book an appointment online`,
    };
  },
  // Salon social sharing
  salonProfile(salon, user, shareUrls) {
    return {
      sms: `I think you'll like ${salon.name}, you can book an appointment online at ${shareUrls.sms}`,
      email: {
        subject: (user && user.first_name)
          ? (`${user.first_name} thinks you'll like StyleSeat`)
          : 'Book your next appointment online with StyleSeat',
        body: `${'Have you checked out StyleSeat? You can book online '
            + 'with the best beauty & wellness professionals in your neighborhood. '
             + "I think you'll like "}${providerAtSalon(salon)}, check it out at`
             + ` ${shareUrls.email}`,
      },
      facebook: {
        header: `Book an appointment online with ${salon.name} | StyleSeat`,
        text: `Book an appointment online with ${providerAtSalon(salon)} in ${salon.city}`,
      },
      image: getProfileShareImage(salon),
      twitter: `I think you'll like ${salon.name}, click the link to book an appointment online`,
    };
  },
};

/**
 * Return the share link for a provider or salon
 * @param {String} the target route
 * @param {Number} the id of the provider
 * @param {Boolean} whether or not to shorten the link
 * @param {Object} list of url variations
 */
const getSharingLinks = (
  linkTo: string,
  providerId: number,
  shorten: boolean,
  queryParamsVariations: Record<string, any>,
) => ssFetchJSON<Record<string, string>>(
  '/api/v1/sharing_links', {
    method: 'POST',
    body: JSON.stringify({
      link_to: linkTo,
      provider: providerId,
      shorten,
      query_params_variations: queryParamsVariations,
    }),
  },
);

export const getShareText = (
  url: string,
  content: string,
  isMine?: boolean,
  customText?: string,
) => {
  if (customText) {
    return `${customText}: ${url}`;
  }

  if (isMine) {
    return `${IS_MINE_MESSAGE}: ${url}`;
  }

  return content;
};

export {
  providerAtSalon,
  getProfileShareImage,
  getSharingLinks,
  urls,
  messages,
};
